import { Box } from '@iheartjane/reefer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import type { BannerCarouselProps } from './bannerCarousel.types';
import { CarouselDots } from './carouselDots/carouselDots';
import { CarouselItem } from './carouselItem/carouselItem';
import { CarouselPlayPause } from './carouselPlayPause/carouselPlayPause';

import styles from './bannerCarousel.module.css';

const DEFAULT_AUTO_ADVANCE_MS = 7000;

// Wrap-around via cloned edge slides: render [clone(last), ...real, clone(first)]
// so advancing off an end animates into a clone, then snaps to the real slide.

/**
 * Auto-advancing banner carousel: swipe/drag, ←/→ keys, dot clicks, and an
 * auto-advance interval, with cloned-edge wrap-around (see above).
 */
export const BannerCarousel = ({
  ariaLabel,
  autoAdvanceMs = DEFAULT_AUTO_ADVANCE_MS,
  className,
  disableAutoAdvance = false,
  disableKeyboardNavigation = false,
  slides,
  ...marginProps
}: BannerCarouselProps) => {
  const totalItems = slides.length;
  const isLooping = totalItems > 1;
  const isAutoAdvancing = isLooping && !disableAutoAdvance;

  // real slides at positions 1..totalItems; clones at 0 and totalItems+1
  const [position, setPosition] = useState(1);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleTogglePlayPause = () => setIsPaused((prev) => !prev);

  // active slide as a logical 0-based index
  const activeIndex = isLooping
    ? (((position - 1) % totalItems) + totalItems) % totalItems
    : 0;

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // paused: don't run or restart (manual nav still navigates, just won't resume)
    if (disableAutoAdvance || !isLooping || isPaused) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setTransitionEnabled(true);
      setPosition((previousPosition) => previousPosition + 1);
    }, autoAdvanceMs);
  }, [autoAdvanceMs, disableAutoAdvance, isLooping, isPaused]);

  // jump straight to a slide (dot click); resets the interval
  const updateIndex = (newIndex: number) => {
    setTransitionEnabled(true);
    setPosition(newIndex + 1);
    startAutoSlide();
  };

  const goNext = useCallback(() => {
    setTransitionEnabled(true);
    setPosition((previousPosition) => previousPosition + 1);
    startAutoSlide();
  }, [startAutoSlide]);

  const goPrev = useCallback(() => {
    setTransitionEnabled(true);
    setPosition((previousPosition) => previousPosition - 1);
    startAutoSlide();
  }, [startAutoSlide]);

  const handleTrackTransitionEnd = () => {
    if (position >= totalItems + 1) {
      setTransitionEnabled(false);
      setPosition(1);
    }

    if (position <= 0) {
      setTransitionEnabled(false);
      setPosition(totalItems);
    }
  };

  // re-enable transition after a snap so the next move animates
  useEffect(() => {
    if (transitionEnabled) {
      return;
    }

    const timeout = setTimeout(() => {
      setTransitionEnabled(true);
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [transitionEnabled]);

  useEffect(() => {
    startAutoSlide();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startAutoSlide]);

  const touchHandlers = useSwipeable({
    onSwipedLeft: () => {
      goNext();
    },
    onSwipedRight: () => {
      goPrev();
    },
    trackMouse: true,
  });

  useEffect(() => {
    if (disableKeyboardNavigation) {
      return;
    }

    const arrowKeyHandlers = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goPrev();
      }

      if (event.key === 'ArrowRight') {
        goNext();
      }
    };

    window.addEventListener('keyup', arrowKeyHandlers);

    return () => {
      window.removeEventListener('keyup', arrowKeyHandlers);
    };
  }, [disableKeyboardNavigation, goNext, goPrev]);

  const handleCarouselItemClick = (index: number) => () => {
    updateIndex(index);
  };

  // [clone(last), ...real, clone(first)] when looping
  const trackSlides = isLooping
    ? [slides[totalItems - 1], ...slides, slides[0]]
    : slides;

  const trackOffset = (isLooping ? position : 0) * 100;

  return (
    <Box
      ariaLabel={ariaLabel}
      className={className}
      data-testid="banner-carousel"
      {...marginProps}
    >
      <Box className={styles['carouselViewport']}>
        <div
          className={styles['carouselTrack']}
          onTransitionEnd={handleTrackTransitionEnd}
          style={{
            transform: `translateX(-${trackOffset}%)`,
            transition: transitionEnabled ? undefined : 'none',
          }}
          {...touchHandlers}
        >
          {trackSlides.map((slide, trackIndex) => {
            // map track position back to its real slide
            const logicalIndex = isLooping
              ? (((trackIndex - 1) % totalItems) + totalItems) % totalItems
              : trackIndex;

            return (
              <CarouselItem
                key={trackIndex}
                activeIndex={activeIndex}
                fallbackSrc={slide.fallbackSrc}
                imageAlt={slide.imageAlt}
                imageSrc={slide.imageSrc}
                index={logicalIndex}
                onClick={handleCarouselItemClick(logicalIndex)}
                srcSet={slide.srcSet}
                url={slide.url}
              />
            );
          })}
        </div>

        {/* sibling of the track so it doesn't move with slides or catch swipes */}
        {isAutoAdvancing && (
          <CarouselPlayPause
            isPaused={isPaused}
            onToggle={handleTogglePlayPause}
          />
        )}
      </Box>

      <CarouselDots
        activeIndex={activeIndex}
        autoAdvanceMs={disableAutoAdvance ? undefined : autoAdvanceMs}
        count={totalItems}
        isPaused={isPaused}
        onSelect={updateIndex}
      />
    </Box>
  );
};
